import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useWatch } from 'react-hook-form'
import {
  Grid,
  TextField,
  Typography,
  DatePicker,
  FormLabel,
  Select,
} from '@the-platform-group/component-library'
import { AsyncSelect } from '@the-platform-group/component-library/Select'
import { debouncedLoadAdminUsers } from 'utils/optionsLoaders'
import { humanizeSnakeCase } from 'utils/format'
import { STATUSES, PRIORITY_OPTIONS } from '../utils/constants'
import { IRWIN_ASSISTANT_STATUS_MAPPING } from '@the-platform-group/component-library/IrwinAssistant'

/**
 * Form component for managing request details
 * @param {object} props - component props
 * @param {object} props.control - react-hook-form control object
 * @param {object} props.formState - react-hook-form formState object
 * @param {function} props.setValue - react-hook-form setValue function
 * @param {object} props.request - the request being managed
 * @returns {React.ReactElement} The rendered ManageRequestForm component
 */
const ManageRequestForm = ({ control, formState, setValue, request }) => {
  const { errors } = formState

  // Watch assignee and status to determine if additional fields should be shown or disabled
  const assignee = useWatch({ control, name: 'assignee' })
  const customer = useWatch({ control, name: 'customer' })
  const status = useWatch({ control, name: 'status' })

  const loadAssignees = useMemo(() => {
    return term =>
      debouncedLoadAdminUsers({
        term,
      })
  }, [])

  const statusOptions = STATUSES.map(status => ({
    value: status,
    label: humanizeSnakeCase(status),
  }))

  const priorityOptions = Object.keys(PRIORITY_OPTIONS).map(priority => ({
    value: priority,
    label: humanizeSnakeCase(priority),
  }))

  return (
    <>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Add or update details as needed for this request.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Please note: These changes will apply to the respective customer account. Please review and
        confirm that the information is accurate before proceeding.
      </Typography>

      <Grid container spacing={2}>
        {/* Read-only Customer Name */}
        <Grid item xs={12}>
          <FormLabel htmlFor="customerName">Customer Name</FormLabel>
          <TextField
            id="customerName"
            variant="outlined"
            fullWidth
            value={customer?.name || ''}
            disabled
            sx={{ mt: 1, backgroundColor: 'grey.100' }}
          />
        </Grid>

        {/* Read-only Request Name */}
        <Grid item xs={12}>
          <FormLabel htmlFor="requestName">Request Name</FormLabel>
          <TextField
            id="requestName"
            variant="outlined"
            fullWidth
            value={request?.name || ''}
            disabled
            sx={{ mt: 1, backgroundColor: 'grey.100' }}
          />
        </Grid>

        {/* Assignee Selection */}
        <Grid item xs={12}>
          <FormLabel htmlFor="assignee">Assignee</FormLabel>
          <Controller
            name="assignee"
            control={control}
            rules={{ required: 'Assignee is required to manage this request' }}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                id="assignee"
                variant="outlined"
                fullWidth
                hideArrow
                includeClearAll
                placeholder="Select assignee"
                optionsLoader={loadAssignees}
                {...(errors.assignee && { error: true })}
                helperText={errors.assignee?.message}
                margin="none"
                sx={{ mt: 1 }}
              />
            )}
          />
        </Grid>

        {/* Fields shown only after assignee is selected */}
        {assignee && (
          <>
            {/* Status */}
            <Grid item xs={12}>
              <FormLabel htmlFor="status">Status</FormLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="status"
                    variant="outlined"
                    fullWidth
                    options={statusOptions}
                    placeholder="Select status"
                    sx={{ mt: 1 }}
                    helperText="This field will be visible to clients."
                  />
                )}
              />
            </Grid>

            {/* Priority */}
            <Grid item xs={12}>
              <FormLabel htmlFor="priority">Priority</FormLabel>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="priority"
                    variant="outlined"
                    fullWidth
                    options={priorityOptions}
                    placeholder="Select priority"
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>

            {/* Expected Completion Date */}
            <Grid item xs={12}>
              <FormLabel htmlFor="expectedCompletionDate">Expected Completion Date</FormLabel>
              <Controller
                name="expectedCompletionDate"
                control={control}
                rules={{ required: 'Expected completion date is required to manage this request' }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    id="expectedCompletionDate"
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 1 }}
                    helperText="This field will be visible to clients."
                    disabled={status === IRWIN_ASSISTANT_STATUS_MAPPING.completed[0]}
                  />
                )}
              />
            </Grid>

            {/* Internal Comments */}
            <Grid item xs={12}>
              <FormLabel htmlFor="internalComments">Internal Comments</FormLabel>
              <Controller
                name="internalComments"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="internalComments"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add internal comments for team visibility"
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>

            {/* Customer Comments */}
            <Grid item xs={12}>
              <FormLabel htmlFor="customerComments">Customer Comments</FormLabel>
              <Controller
                name="customerComments"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="customerComments"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add comments visible to the customer"
                    sx={{ mt: 1 }}
                    helperText="This field will be visible to clients."
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

ManageRequestForm.propTypes = {
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  request: PropTypes.object,
}

export default ManageRequestForm
